// recent Fleet Assistant questions, for the horizon bar ask box's typeahead.
//
// SYNCED per-user, server side, under the same preferences map as site_theme
// and nav_favorites, so the list follows the user across devices, browsers and
// sessions instead of piling up separately in each browser.
//
// localStorage stays the read path rather than becoming a stale mirror: the
// typeahead re-matches on every keystroke and cannot await a round trip, and
// localStorage is synchronous, so it is the cache. the server list is folded
// into it on hydrate and every local write is pushed back, debounced.
//
// entries are { q, ts } with ts in epoch millis. the timestamp is what makes
// the cross-device merge correct -- each device holds a partial history, and
// "whose order wins" has no answer without per-entry recency. the plain-string
// arrays this module used to write are still read (as ts 0), so nobody's
// existing list disappears on upgrade.
//
// this list is written by fleet_assistant.vue whenever a question is actually
// sent, so it covers the panel and the full-page assistant too, not just the
// bar. it is a convenience, never a record: every failure path degrades to a
// shorter list rather than an error.
import { get_recent_questions, update_recent_questions } from '@/networking/apis'

const STORAGE_KEY = 'faRecentQuestions'
const MAX = 25
// long pastes are not questions anyone wants to re-pick from a menu
const MAX_LEN = 300

// a destination entry replays as navigation, so it stores the RESOLVED router
// target rather than the page or object row it came from: pages and objects
// resolve to a destination by different routes (a registry entry vs a
// per-type lookup that needs the tenant), and neither of those is still
// around when the list is read back on another device.
function normalizeTo(raw) {
  if (!raw || typeof raw !== 'object') return null
  if (typeof raw.path === 'string' && raw.path) return { path: raw.path.slice(0, 400) }
  if (typeof raw.name !== 'string' || !raw.name) return null
  const to = { name: raw.name.slice(0, 200) }
  if (raw.query && typeof raw.query === 'object') {
    const query = {}
    Object.keys(raw.query).forEach(k => {
      const v = raw.query[k]
      if (typeof v === 'string' || typeof v === 'number') query[k] = String(v).slice(0, 200)
    })
    if (Object.keys(query).length) to.query = query
  }
  return to
}

// accepts three shapes: the legacy plain-string array, the { q, ts } questions
// this module wrote first, and { q, ts, kind, to, icon } destinations. legacy
// entries get ts 0 so anything timestamped sorts above them, which is right --
// they predate the upgrade by definition.
//
// q doubles as the display label and the dedupe key for both kinds, so one
// list holds the whole history in recency order. an entry with no `kind` is a
// question (asks the assistant); one with a kind navigates.
export function normalizeEntries(raw) {
  if (!Array.isArray(raw)) return []
  const out = []
  const seen = new Set()
  raw.forEach(item => {
    let q = null
    let ts = 0
    let kind = null
    let to = null
    let icon = null
    let external = false
    if (typeof item === 'string') {
      q = item
    } else if (item && typeof item === 'object' && typeof item.q === 'string') {
      q = item.q
      ts = typeof item.ts === 'number' && isFinite(item.ts) ? item.ts : 0
      if (item.kind === 'page' || item.kind === 'object') {
        to = normalizeTo(item.to)
        // a destination with no reachable target is not a destination; drop
        // it rather than keeping a row that does nothing when picked
        if (!to) return
        kind = item.kind
        icon = typeof item.icon === 'string' ? item.icon.slice(0, 60) : null
        external = !!item.external
      }
    }
    if (!q) return
    q = q.trim()
    if (!q || q.length > MAX_LEN) return
    const key = (kind ? kind + ':' : 'q:') + q.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    const entry = { q, ts }
    if (kind) {
      entry.kind = kind
      entry.to = to
      if (icon) entry.icon = icon
      if (external) entry.external = true
    }
    out.push(entry)
  })
  return out
}

// questions and destinations live in one list but are separate histories:
// asking "fleet map" and navigating to Fleet Map are different acts and both
// deserve a row.
function entryKey(e) {
  return (e.kind ? e.kind + ':' : 'q:') + e.q.toLowerCase()
}

// union by question text, keeping the NEWER timestamp for one both sides know.
// neither side is authoritative: the server holds what other devices asked,
// this device holds what it asked before the hydrate landed, and both are real
// history. stable within equal timestamps so the legacy (ts 0) block keeps the
// order it was stored in.
export function mergeEntries(a, b) {
  const byKey = new Map()
  ;(a || []).concat(b || []).forEach(e => {
    const key = entryKey(e)
    const existing = byKey.get(key)
    if (!existing || e.ts > existing.ts) byKey.set(key, e)
  })
  const list = Array.from(byKey.values())
  return list
    .map((e, i) => [e, i])
    .sort((x, y) => (y[0].ts - x[0].ts) || (x[1] - y[1]))
    .map(pair => pair[0])
    .slice(0, MAX)
}

function read() {
  try {
    if (!window.localStorage) return []
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return normalizeEntries(JSON.parse(raw))
  } catch (_) {
    // unparseable or storage blocked (private mode / disabled cookies)
    return []
  }
}

function write(list) {
  try {
    if (!window.localStorage) return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch (_) {
    // storage full or blocked -- the typeahead is optional, so drop it
  }
}

// questions only, as plain strings -- the shape this module has always
// returned to callers that just want the asked history
export function recentQuestions() {
  return read().filter(e => !e.kind).map(e => e.q)
}

function remember(entry) {
  const key = entryKey(entry)
  const next = [entry]
    .concat(read().filter(e => entryKey(e) !== key))
    .slice(0, MAX)
  write(next)
  persist()
}

// newest first, case-insensitively de-duplicated: re-asking something moves it
// back to the top rather than adding a second entry.
export function rememberQuestion(text) {
  const q = (text || '').trim()
  if (!q || q.length > MAX_LEN) return
  remember({ q, ts: Date.now() })
}

// a page or object the user picked out of the ask box. recorded so the box
// has something to offer someone who only ever uses it to jump around, rather
// than opening onto nothing for them forever.
export function rememberDestination({ label, kind, to, icon, external } = {}) {
  const q = (label || '').trim()
  if (!q || q.length > MAX_LEN) return
  if (kind !== 'page' && kind !== 'object') return
  const target = normalizeTo(to)
  if (!target) return
  const entry = { q, ts: Date.now(), kind, to: target }
  if (icon) entry.icon = String(icon).slice(0, 60)
  if (external) entry.external = true
  remember(entry)
}

// substring match, newest first, as full entries: the bar needs the kind and
// target to know whether a row asks or navigates. an empty query returns the
// most recent, which is what an empty box shows.
export function matchRecents(query, limit = 10) {
  const q = (query || '').trim().toLowerCase()
  const all = read()
  const hits = q ? all.filter(e => e.q.toLowerCase().indexOf(q) !== -1) : all
  // an exact echo of what is already typed is not a suggestion
  return hits.filter(e => e.q.trim().toLowerCase() !== q).slice(0, limit)
}

// questions-only string view of the same match, kept for callers that predate
// destinations being in the list
export function matchQuestions(query, limit = 10) {
  return matchRecents(query, limit).filter(e => !e.kind).map(e => e.q)
}

let loadPromise = null

// fetches once per page load. safe to call from every consumer's mounted().
export function ensureRecentQuestionsLoaded() {
  if (loadPromise) return loadPromise
  loadPromise = (async () => {
    // no session yet (login screen): nothing to fetch, and a call here would
    // 403 straight into networking/index.js's clear-and-bounce handler
    try {
      if (!window.localStorage || !window.localStorage.getItem('jwt')) return
    } catch (_) {
      return
    }
    try {
      const { value: { data = {} } = {} } = await get_recent_questions()
      const remote = normalizeEntries(data.recent_questions)
      const local = read()
      write(mergeEntries(remote, local))
      // push back only when this device carries something the server has not
      // seen -- a plain hydrate should not cost a write. covers the first run
      // after the upgrade (local list, empty server) and anything asked while
      // the fetch was in flight.
      const unseen = local.some(
        e => !remote.some(r => r.q.toLowerCase() === e.q.toLowerCase())
      )
      if (unseen) persist()
    } catch (_) {
      // recents are a convenience; a failed fetch must never block the bar
    }
  })()
  return loadPromise
}

// coalesce bursts into one write, and keep at most ONE in flight carrying the
// latest state: the payload is a full replacement, so overlapping writes could
// land out of order (same reasoning as nav_favorites.persist).
let saveTimer = null
let writing = false
let pending = false

function persist() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(flush, 400)
}

async function flush() {
  saveTimer = null
  try {
    if (!window.localStorage || !window.localStorage.getItem('jwt')) return
  } catch (_) {
    return
  }
  if (writing) {
    pending = true
    return
  }
  writing = true
  try {
    await update_recent_questions(read())
  } catch (_) {
    // dropped on the floor: localStorage still has it, and the next question
    // asked re-sends the whole list
  } finally {
    writing = false
    if (pending) {
      pending = false
      persist()
    }
  }
}
